import {
  buildIndicatorTemplate,
  getInstanceChartName,
  type IndicatorInstanceSettings,
} from "@/lib/chart-indicators/registry";
import {
  buildCustomTemplatesIfCompiled,
  clearCustomComputeCache,
  deinitCustomIndicatorWasmInstances,
  getCompiledMetaForProject,
  MAIN_PANE_BG_KEY,
  MAIN_PANE_KEY,
  runCustomIndicatorLifecycle,
  type CustomPaneDescriptor,
} from "@/lib/custom-indicators/compiled-template";
import {
  createCustomNoOpTemplate,
  isCustomIndicatorId,
} from "@/lib/custom-indicators/no-op-template";
import { isEditorPreviewInstanceId } from "@/lib/custom-indicators/editor-preview-ids";
import { getCustomIndicatorIdFromInstanceId } from "@/lib/custom-indicators/indicator-source";
import { useIndicatorCompileStore } from "@/lib/custom-indicators/runtime/compile-store";
import type { CompileResult } from "@/lib/custom-indicators/runtime-types";
import { ensureCompiledFromBuild } from "@/lib/custom-indicators/hydrate-build";

import { klinecharts } from "./klinecharts-client";

const MAIN_PANE_ID = "candle_pane";

export interface AppliedSubIndicator {
  paneKey: string;
  paneId: string;
  name: string;
}

/** Per-instance state applied on the KLine chart */
export interface AppliedIndicatorState {
  name: string;
  paneId: string;
  valuesHash: string;
  stylesHash: string;
  visible: boolean;
  /** Multi-pane custom indicators — one KLine registration per pane */
  subIndicators?: AppliedSubIndicator[];
  /** For custom WASM indicators — deinit on remove */
  indicatorId?: string;
  inputValues?: Record<string, number | string | boolean>;
  /** Bumps when Roslyn compile succeeds — triggers template re-register */
  compileRevision?: number;
}

export type IndicatorSyncStateMap = Map<string, AppliedIndicatorState>;

export interface IndicatorSyncItem {
  id: string;
  indicatorId: string;
  /** Display name (used for custom indicator legend) */
  name?: string;
  settings?: {
    values?: Record<string, number | string>;
    styles?: Record<string, { color: string; style: string }>;
    styleSettings?: Record<string, { color: string; style: string }>;
  };
  visible?: boolean;
}

/** Minimal KLine chart surface used for indicator sync */
export type ChartIndicatorApi = {
  createIndicator: (
    value: string | Record<string, unknown>,
    isStack?: boolean,
    paneOptions?: { id?: string; height?: number }
  ) => string | null | void;
  overrideIndicator?: (
    override: Record<string, unknown>,
    paneId?: string
  ) => void;
  removeIndicator: (paneId: string, name?: string) => void;
  getIndicatorByPaneId?: () => unknown;
  getDataList?: () => Array<Record<string, unknown>>;
  updateData?: (bar: Record<string, unknown>) => void;
  resize?: () => void;
};

/** Nudge KLineCharts to re-run indicator calc (resize alone is not always enough). */
export function forceChartIndicatorRecalc(chart: ChartIndicatorApi | null): void {
  if (!chart) return;
  chart.resize?.();
  const list = chart.getDataList?.();
  if (!list?.length) return;
  const last = list[list.length - 1];
  if (last) chart.updateData?.({ ...last });
}

function hashRecord(record: Record<string, unknown> | undefined): string {
  if (!record || Object.keys(record).length === 0) return "";
  return JSON.stringify(record);
}

function normalizeSettings(settings?: IndicatorSyncItem["settings"]): {
  values: Record<string, number | string>;
  styles: Record<string, { color: string; style: string }>;
} {
  if (!settings) {
    return { values: {}, styles: {} };
  }
  const styles =
    settings.styles ??
    settings.styleSettings ??
    ({} as Record<string, { color: string; style: string }>);
  return {
    values: settings.values ?? {},
    styles,
  };
}

function buildTemplatePayload(
  indicatorId: string,
  instanceId: string,
  settings?: IndicatorInstanceSettings,
  displayName?: string
) {
  let template;
  if (isCustomIndicatorId(indicatorId)) {
    const panes = buildCustomTemplatesIfCompiled(
      indicatorId,
      instanceId,
      displayName ?? indicatorId,
      settings
    );
    template =
      panes?.find((p) => p.isMain)?.template ??
      panes?.[0]?.template ??
      createCustomNoOpTemplate(displayName ?? indicatorId);
  } else {
    template = buildIndicatorTemplate(indicatorId, settings);
  }
  const instanceName = getInstanceChartName(indicatorId, instanceId);
  template.name = instanceName;
  if (displayName) {
    template.shortName = displayName;
  }
  return { template, instanceName };
}

function sanitizePaneKeyForName(paneKey: string): string {
  return paneKey.replace(/[^a-zA-Z0-9_]/g, "_");
}

/** Candle/signal/zone/profile/dashboard draw must stack on the price pane. */
export function hasMainPaneDrawFeatures(meta?: CompileResult): boolean {
  if (!meta?.success) return false;
  return !!(
    meta.dashboard?.cells?.length ||
    meta.candleColor ||
    meta.signal ||
    (meta.signals?.length ?? 0) > 0 ||
    meta.zone ||
    meta.priceHistogram
  );
}

/** Whether custom indicator main pane should overlay candle_pane on the primary chart. */
export function resolveCustomMainChartOverlay(meta?: CompileResult): boolean {
  return meta?.overlay !== false || hasMainPaneDrawFeatures(meta);
}

function getCustomPaneChartName(
  baseName: string,
  paneKey: string,
  isMain: boolean
): string {
  if (paneKey === MAIN_PANE_BG_KEY) return `${baseName}__bg`;
  if (isMain) return baseName;
  return `${baseName}__p_${sanitizePaneKeyForName(paneKey)}`;
}

function getAppliedSubIndicators(
  applied: AppliedIndicatorState
): AppliedSubIndicator[] {
  if (applied.subIndicators?.length) return applied.subIndicators;
  return [
    {
      paneKey: MAIN_PANE_KEY,
      paneId: applied.paneId,
      name: applied.name,
    },
  ];
}

export function removeAppliedIndicatorFromChart(
  chart: ChartIndicatorApi,
  applied: AppliedIndicatorState
): void {
  for (const sub of getAppliedSubIndicators(applied)) {
    try {
      chart.removeIndicator(sub.paneId, sub.name);
    } catch (error) {
      console.error(`Failed to remove indicator ${sub.name}:`, error);
    }
  }
}

function hasCompiledChartTemplate(projectId: string): boolean {
  const meta = getCompiledMetaForProject(projectId);
  if (!meta?.success) return false;
  const hasPlots = (meta.plots?.length ?? 0) > 0;
  const hasMainDraw =
    !!meta.dashboard?.cells?.length ||
    meta.candleColor ||
    meta.signal ||
    (meta.signals?.length ?? 0) > 0 ||
    meta.zone ||
    meta.priceHistogram;
  return hasPlots || Boolean(hasMainDraw);
}

function buildCustomPaneDescriptors(
  indicatorId: string,
  instanceId: string,
  displayName: string,
  settings?: IndicatorInstanceSettings
): CustomPaneDescriptor[] {
  const compiled =
    buildCustomTemplatesIfCompiled(
      indicatorId,
      instanceId,
      displayName,
      settings
    ) ?? [];
  if (compiled.length > 0) return compiled;
  return [
    {
      paneKey: MAIN_PANE_KEY,
      isMain: true,
      template: createCustomNoOpTemplate(displayName) as Record<string, unknown>,
    },
  ];
}

function paneStructureChanged(
  applied: AppliedIndicatorState,
  descriptors: CustomPaneDescriptor[]
): boolean {
  const oldKeys = new Set(
    getAppliedSubIndicators(applied).map((s) => s.paneKey)
  );
  const newKeys = new Set(descriptors.map((d) => d.paneKey));
  if (oldKeys.size !== newKeys.size) return true;
  for (const key of newKeys) {
    if (!oldKeys.has(key)) return true;
  }
  return false;
}

function attachCustomIndicatorPanes(
  chart: ChartIndicatorApi,
  baseName: string,
  displayName: string | undefined,
  descriptors: CustomPaneDescriptor[],
  overlayMain: boolean,
  visible: boolean
): AppliedSubIndicator[] {
  const subIndicators: AppliedSubIndicator[] = [];

  for (const desc of descriptors) {
    const regName = getCustomPaneChartName(baseName, desc.paneKey, desc.isMain);
    const template = { ...desc.template };
    template.name = regName;
    if (displayName) {
      template.shortName =
        desc.paneKey === MAIN_PANE_BG_KEY
          ? ""
          : desc.isMain && desc.paneKey === MAIN_PANE_KEY
            ? displayName
            : `${displayName} (${desc.paneKey === MAIN_PANE_KEY ? "Main" : desc.paneKey})`;
    }

    registerInstanceTemplate(
      template as unknown as ReturnType<typeof buildIndicatorTemplate>
    );

    let paneId: string;
    if (desc.isMain && overlayMain) {
      paneId = MAIN_PANE_ID;
      chart.createIndicator(regName, true, { id: paneId });
    } else {
      const paneOpts: { height?: number } = {};
      if (desc.height != null && desc.height > 0) {
        paneOpts.height = desc.height;
      }
      const created = chart.createIndicator(regName, false, paneOpts);
      paneId =
        typeof created === "string" && created.length > 0
          ? created
          : resolvePaneIdForIndicator(chart, regName) ?? regName;
    }

    if (!visible) {
      chart.overrideIndicator?.({ name: regName, visible: false }, paneId);
    }

    subIndicators.push({ paneKey: desc.paneKey, paneId, name: regName });
  }

  return subIndicators;
}

function syncCustomIndicatorItem(
  chart: ChartIndicatorApi,
  options: { isOverlay: boolean },
  item: IndicatorSyncItem,
  applied: AppliedIndicatorState | undefined,
  state: IndicatorSyncStateMap
): boolean {
  const visible = item.visible !== false;
  const { values, styles } = normalizeSettings(item.settings);
  const valuesHash = hashRecord(values as Record<string, unknown>);
  const stylesHash = hashRecord(styles as Record<string, unknown>);
  const projectId = getCustomIndicatorIdFromInstanceId(item.indicatorId);
  const compileMeta = projectId
    ? useIndicatorCompileStore.getState().getCompileResult(projectId)
    : undefined;
  const compileRevision = projectId
    ? useIndicatorCompileStore.getState().getCompileRevision(projectId)
    : 0;
  const overlayMain =
    options.isOverlay && resolveCustomMainChartOverlay(compileMeta);
  const baseName = getInstanceChartName(item.indicatorId, item.id);
  const displayName = item.name ?? item.indicatorId;
  const descriptors = buildCustomPaneDescriptors(
    item.indicatorId,
    item.id,
    displayName,
    item.settings as IndicatorInstanceSettings | undefined
  );

  const compileChanged =
    applied != null && applied.compileRevision !== compileRevision;

  if (
    applied &&
    compileChanged &&
    projectId &&
    hasCompiledChartTemplate(projectId)
  ) {
    if (!isEditorPreviewInstanceId(item.id)) {
      void deinitCustomIndicatorWasmInstances(
        projectId,
        item.id,
        applied.inputValues ?? {}
      );
    }
    clearCustomComputeCache(item.id);
    removeAppliedIndicatorFromChart(chart, applied);
    state.delete(item.id);
    applied = undefined;
  }

  if (applied && paneStructureChanged(applied, descriptors)) {
    removeAppliedIndicatorFromChart(chart, applied);
    state.delete(item.id);
    applied = undefined;
  }

  if (!applied) {
    const subIndicators = attachCustomIndicatorPanes(
      chart,
      baseName,
      item.name,
      descriptors,
      overlayMain,
      visible
    );
    const mainSub =
      subIndicators.find((s) => s.paneKey === MAIN_PANE_KEY) ?? subIndicators[0]!;

    const appliedState: AppliedIndicatorState = {
      name: baseName,
      paneId: mainSub.paneId,
      subIndicators,
      valuesHash,
      stylesHash,
      visible,
      indicatorId: item.indicatorId,
      inputValues: values as Record<string, number | string | boolean>,
      compileRevision,
    };

    if (projectId && !isEditorPreviewInstanceId(item.id)) {
      void runCustomIndicatorLifecycle(
        projectId,
        item.id,
        appliedState.inputValues!,
        "init"
      );
    }

    state.set(item.id, appliedState);
    forceChartIndicatorRecalc(chart);
    return true;
  }

  const valuesChanged = applied.valuesHash !== valuesHash;
  const stylesChanged = applied.stylesHash !== stylesHash;
  const visibleChanged = applied.visible !== visible;

  if (!valuesChanged && !stylesChanged && !visibleChanged && !compileChanged) {
    return false;
  }

  if (compileChanged) {
    clearCustomComputeCache(item.id);
  }

  const subs = getAppliedSubIndicators(applied);
  for (const desc of descriptors) {
    const sub = subs.find((s) => s.paneKey === desc.paneKey);
    if (!sub) continue;

    const regName = sub.name;
    const template = { ...desc.template };
    template.name = regName;
    if (item.name) {
      template.shortName =
        desc.paneKey === MAIN_PANE_BG_KEY
          ? ""
          : desc.isMain && desc.paneKey === MAIN_PANE_KEY
            ? item.name
            : `${item.name} (${desc.paneKey === MAIN_PANE_KEY ? "Main" : desc.paneKey})`;
    }

    registerInstanceTemplate(
      template as unknown as ReturnType<typeof buildIndicatorTemplate>
    );

    if (valuesChanged || compileChanged) {
      chart.overrideIndicator?.(
        toOverridePayload(
          template as unknown as ReturnType<typeof buildIndicatorTemplate>,
          visible
        ),
        sub.paneId
      );
    } else if (stylesChanged) {
      chart.overrideIndicator?.(
        toOverridePayload(
          template as unknown as ReturnType<typeof buildIndicatorTemplate>,
          visible
        ),
        sub.paneId
      );
    } else if (visibleChanged) {
      chart.overrideIndicator?.({ name: regName, visible }, sub.paneId);
    }
  }

  state.set(item.id, {
    ...applied,
    valuesHash,
    stylesHash,
    visible,
    compileRevision,
    inputValues: values as Record<string, number | string | boolean>,
  });

  if (compileChanged || valuesChanged) {
    if (
      compileChanged &&
      projectId &&
      !isEditorPreviewInstanceId(item.id) &&
      hasCompiledChartTemplate(projectId)
    ) {
      void runCustomIndicatorLifecycle(
        projectId,
        item.id,
        values as Record<string, number | string | boolean>,
        "init"
      );
    }
    forceChartIndicatorRecalc(chart);
  }
  return true;
}

function registerInstanceTemplate(template: ReturnType<typeof buildIndicatorTemplate>) {
  if (!klinecharts) return;
  (klinecharts as unknown as { registerIndicator: (def: unknown) => void }).registerIndicator(
    template
  );
}

function toOverridePayload(
  template: ReturnType<typeof buildIndicatorTemplate>,
  visible: boolean
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: template.name,
    shortName: template.shortName,
    calcParams: template.calcParams,
    styles: template.styles,
    figures: template.figures,
    visible,
  };
  const zLevel = (template as { zLevel?: number }).zLevel;
  if (zLevel != null) {
    payload.zLevel = zLevel;
  }
  const drawFn = (template as { draw?: unknown }).draw;
  if (typeof drawFn === "function") {
    payload.draw = drawFn;
  }
  return payload;
}

/**
 * Incrementally sync store indicators onto a KLine chart instance.
 * @param chart - KLine chart API
 * @param options.isOverlay - true for main-pane overlays (candle_pane + stack)
 * @param nextList - desired indicator instances from Zustand
 * @param state - mutable map of last-applied state keyed by instance id
 */
const hydrateScheduled = new Set<string>();

function scheduleHydrateCustomProjects(
  chart: ChartIndicatorApi,
  options: { isOverlay: boolean },
  nextList: IndicatorSyncItem[],
  state: IndicatorSyncStateMap
): void {
  const projectIds = new Set<string>();
  for (const item of nextList) {
    if (!isCustomIndicatorId(item.indicatorId)) continue;
    const projectId = getCustomIndicatorIdFromInstanceId(item.indicatorId);
    if (!projectId) continue;
    const compiled = useIndicatorCompileStore.getState().getCompileResult(projectId);
    if (compiled?.success) continue;
    projectIds.add(projectId);
  }

  for (const projectId of projectIds) {
    if (hydrateScheduled.has(projectId)) continue;
    hydrateScheduled.add(projectId);
    void ensureCompiledFromBuild(projectId)
      .then((result) => {
        if (!result?.success) return;
        forceChartIndicatorRecalc(chart);
        for (const item of nextList) {
          if (!isCustomIndicatorId(item.indicatorId)) continue;
          const pid = getCustomIndicatorIdFromInstanceId(item.indicatorId);
          if (pid !== projectId) continue;
          try {
            syncCustomIndicatorItem(
              chart,
              { isOverlay: options.isOverlay },
              item,
              state.get(item.id),
              state
            );
          } catch (error) {
            console.error(
              `Failed to re-sync custom indicator after hydrate ${projectId}:`,
              error
            );
          }
        }
      })
      .finally(() => {
        hydrateScheduled.delete(projectId);
      });
  }
}

export function syncIndicators(
  chart: ChartIndicatorApi | null,
  options: { isOverlay: boolean },
  nextList: IndicatorSyncItem[],
  state: IndicatorSyncStateMap
): boolean {
  if (!chart || !klinecharts) return false;

  scheduleHydrateCustomProjects(chart, options, nextList, state);

  let changed = false;
  const nextIds = new Set(nextList.map((i) => i.id));

  // Remove instances no longer in the store
  for (const [instanceId, applied] of [...state.entries()]) {
    if (!nextIds.has(instanceId)) {
      if (applied.indicatorId && isCustomIndicatorId(applied.indicatorId)) {
        const projectId = getCustomIndicatorIdFromInstanceId(applied.indicatorId);
        if (projectId) {
          void deinitCustomIndicatorWasmInstances(
            projectId,
            instanceId,
            applied.inputValues ?? {}
          );
        }
        clearCustomComputeCache(instanceId);
      }
      removeAppliedIndicatorFromChart(chart, applied);
      state.delete(instanceId);
      changed = true;
    }
  }

  for (const item of nextList) {
    if (isCustomIndicatorId(item.indicatorId)) {
      try {
        if (
          syncCustomIndicatorItem(chart, options, item, state.get(item.id), state)
        ) {
          changed = true;
        }
      } catch (error) {
        console.error(
          `Failed to sync custom indicator ${item.indicatorId} (${item.id}):`,
          error
        );
      }
      continue;
    }

    const visible = item.visible !== false;
    const { values, styles } = normalizeSettings(item.settings);
    const valuesHash = hashRecord(values as Record<string, unknown>);
    const stylesHash = hashRecord(styles as Record<string, unknown>);
    const applied = state.get(item.id);

    try {
      const { template, instanceName } = buildTemplatePayload(
        item.indicatorId,
        item.id,
        item.settings as IndicatorInstanceSettings | undefined,
        item.name
      );

      if (!applied) {
        registerInstanceTemplate(
          template as unknown as ReturnType<typeof buildIndicatorTemplate>
        );
        let paneId: string;

        if (options.isOverlay) {
          paneId = MAIN_PANE_ID;
          chart.createIndicator(instanceName, true, { id: paneId });
        } else {
          const created = chart.createIndicator(instanceName);
          paneId =
            typeof created === "string" && created.length > 0
              ? created
              : resolvePaneIdForIndicator(chart, instanceName) ?? instanceName;
        }

        if (!visible) {
          chart.overrideIndicator?.(
            { name: instanceName, visible: false },
            paneId
          );
        }

        const appliedState: AppliedIndicatorState = {
          name: instanceName,
          paneId,
          valuesHash,
          stylesHash,
          visible,
        };
        state.set(item.id, appliedState);
        forceChartIndicatorRecalc(chart);
        changed = true;
        continue;
      }

      const valuesChanged = applied.valuesHash !== valuesHash;
      const stylesChanged = applied.stylesHash !== stylesHash;
      const visibleChanged = applied.visible !== visible;

      if (!valuesChanged && !stylesChanged && !visibleChanged) {
        continue;
      }

      registerInstanceTemplate(
        template as unknown as ReturnType<typeof buildIndicatorTemplate>
      );

      if (valuesChanged) {
        chart.overrideIndicator?.(
          toOverridePayload(
            template as unknown as ReturnType<typeof buildIndicatorTemplate>,
            visible
          ),
          applied.paneId
        );
      } else if (stylesChanged) {
        chart.overrideIndicator?.(
          {
            name: instanceName,
            styles: template.styles,
            visible,
          },
          applied.paneId
        );
      } else if (visibleChanged) {
        chart.overrideIndicator?.(
          { name: instanceName, visible },
          applied.paneId
        );
      }

      state.set(item.id, {
        ...applied,
        valuesHash,
        stylesHash,
        visible,
      });

      if (valuesChanged) {
        forceChartIndicatorRecalc(chart);
      }
      changed = true;
    } catch (error) {
      console.error(
        `Failed to sync indicator ${item.indicatorId} (${item.id}):`,
        error
      );
    }
  }

  return changed;
}

/** Clear diff state when the chart instance is disposed (symbol/timeframe change). */
export function clearIndicatorSyncState(state: IndicatorSyncStateMap): void {
  state.clear();
}

function resolvePaneIdForIndicator(
  chart: ChartIndicatorApi,
  instanceName: string
): string | null {
  const chartAny = chart as {
    getIndicatorByPaneId?: () =>
      | Map<string, Map<string, unknown>>
      | Record<string, Record<string, unknown>>
      | null;
  };
  const byPane = chartAny.getIndicatorByPaneId?.();
  if (!byPane) return null;

  if (byPane instanceof Map) {
    for (const [paneId, indicators] of byPane.entries()) {
      if (paneId === MAIN_PANE_ID) continue;
      if (indicators instanceof Map && indicators.has(instanceName)) {
        return paneId;
      }
      if (
        indicators &&
        typeof indicators === "object" &&
        instanceName in indicators
      ) {
        return paneId;
      }
    }
    return null;
  }

  for (const [paneId, indicators] of Object.entries(byPane)) {
    if (paneId === MAIN_PANE_ID) continue;
    if (indicators && typeof indicators === "object" && instanceName in indicators) {
      return paneId;
    }
  }
  return null;
}

/** @deprecated Use syncIndicators — kept for any legacy callers */
export const addIndicatorToChart = (
  chartInstance: ChartIndicatorApi | null,
  indicator: {
    indicatorId: string;
    id: string;
    settings?: IndicatorInstanceSettings;
    visible?: boolean;
  },
  isMainChart: boolean
) => {
  if (!chartInstance) return;
  const state: IndicatorSyncStateMap = new Map();
  syncIndicators(
    chartInstance,
    { isOverlay: isMainChart },
    [indicator],
    state
  );
};
